/**
 * Firestore REST API Property Fetcher
 * 
 * Uses the native Firestore REST API via fetch() instead of the Firebase SDK.
 * This eliminates ALL WebChannel (/Listen/channel) connections, iframes, and
 * streaming infrastructure from public visitor page loads.
 * 
 * Zero Firebase SDK dependency — safe for the critical rendering path.
 */
import { normalizeProperty } from '../utils/propertyNormalizer';

const PROJECT_ID = 'em-asesorias';
const API_KEY = 'AIzaSyCD_Gc-NEam85B8Zj2FRusv83bJ8TeesKw';
const COLLECTION = 'properties';
const BASE_URL = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents`;

/**
 * Converts a Firestore REST API typed value to a plain JS value.
 */
function convertValue(v) {
  if (v == null) return null;
  if ('stringValue' in v) return v.stringValue;
  if ('integerValue' in v) return Number(v.integerValue);
  if ('doubleValue' in v) return v.doubleValue;
  if ('booleanValue' in v) return v.booleanValue;
  if ('nullValue' in v) return null;
  if ('timestampValue' in v) {
    const ts = new Date(v.timestampValue);
    return { seconds: Math.floor(ts.getTime() / 1000) };
  }
  if ('mapValue' in v) return convertFields(v.mapValue.fields || {});
  if ('arrayValue' in v) return (v.arrayValue.values || []).map(convertValue);
  // geoPointValue, referenceValue, bytesValue — uncommon for this use case
  if ('geoPointValue' in v) return { lat: v.geoPointValue.latitude, lng: v.geoPointValue.longitude };
  return null;
}

/**
 * Converts a Firestore REST fields object to a plain JS object.
 */
function convertFields(fields) {
  const result = {};
  for (const [key, value] of Object.entries(fields)) {
    result[key] = convertValue(value);
  }
  return result;
}

/**
 * Extracts the document ID from a Firestore REST document name.
 * e.g. "projects/em-asesorias/databases/(default)/documents/properties/EM-001" → "EM-001"
 */
function extractDocId(documentName) {
  const parts = documentName.split('/');
  return parts[parts.length - 1];
}

/**
 * Fetches all properties from Firestore using the REST API.
 * Returns an array of normalized property objects identical to the SDK version.
 * 
 * This function makes a single HTTP GET request — no WebChannel, no streaming,
 * no persistent connections, no /Listen/channel.
 */
export const fetchPropertiesREST = async () => {
  const url = `${BASE_URL}/${COLLECTION}?key=${API_KEY}&pageSize=300`;

  let allDocuments = [];
  let nextPageToken = null;

  // Handle pagination (unlikely for <100 properties but safe)
  do {
    const pageUrl = nextPageToken ? `${url}&pageToken=${nextPageToken}` : url;
    const response = await fetch(pageUrl);

    if (!response.ok) {
      throw new Error(`Firestore REST API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    const documents = data.documents || [];
    allDocuments = allDocuments.concat(documents);
    nextPageToken = data.nextPageToken || null;
  } while (nextPageToken);

  // Convert each Firestore REST document to our normalized property format
  return allDocuments.map(doc => {
    const docId = extractDocId(doc.name);
    const flatData = convertFields(doc.fields || {});
    return normalizeProperty(docId, flatData);
  });
};
